import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Sun, Moon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister ? { email, password, name } : { email, password };
      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.token, res.data.user);
      toast.success(isRegister ? "Account created" : "Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left: Form */}
      <div className="w-full lg:w-[480px] flex flex-col justify-center px-8 sm:px-12 lg:px-16 bg-background relative">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors"
          data-testid="login-theme-toggle"
        >
          {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        <div className="max-w-sm w-full animate-in" style={{ animationDelay: "0.1s" }}>
          {/* Logo */}
          <div className="mb-12">
            <h1
              className="text-2xl font-bold tracking-tight text-foreground"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              <span className="text-primary">a</span>align
            </h1>
            <p className="text-xs text-muted-foreground mt-1 uppercase tracking-[0.2em]">
              AI Campaign Builder
            </p>
          </div>

          {/* Heading */}
          <h2
            className="text-3xl font-extrabold text-foreground mb-2 tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {isRegister ? "Create Account" : "Sign In"}
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            {isRegister
              ? "Join the aalign platform to start creating campaigns."
              : "Enter your credentials to access the campaign builder."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <label className="field-label">Full Name</label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-transparent border-border text-foreground placeholder:text-muted-foreground h-11 px-4 rounded-sm"
                  required
                />
              </div>
            )}
            <div>
              <label className="field-label">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@aalign.io"
                className="bg-transparent border-border text-foreground placeholder:text-muted-foreground h-11 px-4 rounded-sm"
                required
              />
            </div>
            <div>
              <label className="field-label">Password</label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-transparent border-border text-foreground placeholder:text-muted-foreground h-11 px-4 pr-10 rounded-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-primary hover:bg-primary/85 text-primary-foreground font-medium rounded-sm text-sm active:scale-95 transition-transform"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {isRegister ? "Creating..." : "Signing in..."}
                </span>
              ) : isRegister ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="mt-6 text-xs text-muted-foreground">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-primary hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Sign In" : "Create one"}
            </button>
          </p>
        </div>
      </div>

      {/* Right: Visual */}
      <div
        className="hidden lg:block flex-1 relative"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/3612932/pexels-photo-3612932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
        <div className="absolute bottom-12 left-12 right-12">
          <p
            className="text-4xl font-extrabold text-foreground tracking-tight leading-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Turn campaign briefs
            <br />
            <span className="text-accent">into structured data</span>
            <br />
            in seconds.
          </p>
          <p className="text-sm text-muted-foreground mt-4 max-w-md">
            Powered by AI to map your requirements to aalign&apos;s UDC values automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
